import { RefObject, useCallback, useState } from 'react';

import { differenceInSeconds, secondsToMilliseconds } from 'date-fns';

import { VideoItem } from 'services/data-manager/types';

import { OnProgressEvent, PlayerRef } from 'components/Player/types.ts';

type Params = {
    playerRef: RefObject<PlayerRef | null>;
    videoItem: VideoItem | undefined;
    isCatchup: boolean;
    isStartover: boolean;
};

function usePlayerProgress({
    playerRef,
    videoItem,
    isCatchup,
    isStartover,
}: Params) {
    const [playerProgress, setPlayerProgress] = useState(0);

    const handleDynamicStreamProgressEvent = useCallback(
        ({ absolutePosition }: OnProgressEvent) => {
            if (absolutePosition != null && videoItem?.startTime != null) {
                setPlayerProgress(
                    differenceInSeconds(
                        secondsToMilliseconds(absolutePosition),
                        secondsToMilliseconds(videoItem.startTime),
                    ),
                );
            }
        },
        [videoItem?.startTime],
    );

    const handleStaticStreamProgressEvent = useCallback(
        ({ relativePosition }: OnProgressEvent) => {
            if (relativePosition != null) {
                setPlayerProgress(Math.floor(relativePosition));
            }
        },
        [],
    );

    const onPlayerProgress = useCallback(
        async (event: OnProgressEvent) => {
            const isLiveStream = await playerRef.current?.getIsLive();

            if (isCatchup || isStartover || isLiveStream === false) {
                handleStaticStreamProgressEvent(event);
            } else if (isLiveStream) {
                handleDynamicStreamProgressEvent(event);
            }
        },
        [
            handleDynamicStreamProgressEvent,
            handleStaticStreamProgressEvent,
            isCatchup,
            isStartover,
            playerRef,
        ],
    );

    return {
        playerProgress,
        setPlayerProgress,
        onPlayerProgress,
    };
}

export default usePlayerProgress;
